import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";
import { Building2, Lock } from "lucide-react";
import { paths } from "@shared-core/routing/routes";
import { usePortalSignInAdapter } from "@shared-core/data/PortalSignInAdapterContext";
import { linkedCustomerAccessSummary } from "@shared-core/domain/customerPortalAccess";
import type {
  PortalLinkedCustomer,
  PortalSignInOutcome,
} from "@shared-core/data/customer-portal-access-types";
import { Card } from "../../../app/components/primitives/card";
import { Button } from "../../../app/components/primitives/button";
import { cn } from "../../../app/components/primitives/utils";
import { PortalSignInShell } from "./PortalSignInShell";
import { PortalReviewBar } from "./PortalReviewBar";
import { PortalSignInOutcomeGate } from "./PortalSignInOutcomeGate";

// The chooser for a person linked to several customers (an agency, a group
// head office). The portal always works as ONE customer at a time and never
// adds them together: an agency sees Currys' work or Pret's, so that Currys
// never sees Pret's. The choice is remembered per device only if the person
// ticks "Remember my choice" (unticked by default: 14 Oct 2026, Brad). Once
// remembered, the usual case is no chooser at all (the landing gate uses it
// silently while it is still one of their customers); "Change customer" in the
// header (and ?change=1) brings it back. A customer staff have blocked is named, unavailable, and its
// reason is never shown.
// decision: docs/features/customer-storefront/12_decision_access_for_every_customer.md,
// 13_..._impact_assessment.md Decision 5 (Q5)

type Several = Extract<
  PortalSignInOutcome,
  { kind: "choose_customer" | "welcome" | "ready" }
>;

function ChooserBody({
  outcome,
  scenario,
}: {
  outcome: Several;
  scenario: string | undefined;
}) {
  const adapter = usePortalSignInAdapter();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const changing = searchParams.get("change") === "1";

  const available = outcome.customers.filter((c) => !c.is_blocked);
  const remembered = adapter.getRememberedCustomerId();
  const rememberedCustomer = available.find(
    (c) => c.customer_id === remembered,
  );
  const [selectedId, setSelectedId] = useState<string | null>(
    rememberedCustomer?.customer_id ?? null,
  );
  const [remember, setRemember] = useState(false);
  const autoOpening = !changing && rememberedCustomer !== undefined;

  function open(customer: PortalLinkedCustomer, keep: boolean) {
    adapter.acknowledgeWelcome();
    adapter.chooseCustomer(customer.customer_id, keep);
    // Home: the customer's branded home where the theme read succeeds, Orders otherwise (PortalHomePage decides).
    navigate(paths.portalHome(), { replace: true });
  }

  // The usual case: someone who chose before goes straight in.
  useEffect(() => {
    if (autoOpening && rememberedCustomer) open(rememberedCustomer, true);
    // Runs once on arrival; `open` is recreated every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The old model serving (`ready` with no customers): nothing to choose between.
  if (outcome.customers.length === 0) {
    return <Navigate to={paths.portalHome()} replace />;
  }

  if (autoOpening && rememberedCustomer) {
    return (
      <PortalSignInShell
        title={`Opening ${rememberedCustomer.customer_name}…`}
        subtitle="You chose this customer last time on this device."
        footer={<PortalReviewBar current={scenario} />}
      >
        <p className="text-center text-sm">
          <Link
            className="text-primary underline-offset-4 hover:underline"
            to={paths.portalChooseCustomer({ scenario, change: true })}
          >
            Choose a different customer
          </Link>
        </p>
      </PortalSignInShell>
    );
  }

  const selected = available.find((c) => c.customer_id === selectedId);

  return (
    <PortalSignInShell
      title="Which customer?"
      subtitle={`You're linked to ${outcome.customers.length} customers. You'll see one at a time.`}
      footer={<PortalReviewBar current={scenario} />}
    >
      <div role="radiogroup" aria-label="Customers" className="space-y-2">
        {outcome.customers.map((c) => {
          const isSelected = c.customer_id === selectedId;
          return (
            <button
              key={c.customer_id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={c.is_blocked}
              onClick={() => setSelectedId(c.customer_id)}
              className={cn(
                "flex w-full items-start gap-3 rounded-xl border bg-card p-4 text-left shadow-sm transition-colors",
                isSelected
                  ? "border-primary ring-2 ring-primary/30"
                  : "hover:bg-accent/40",
                c.is_blocked && "cursor-not-allowed opacity-70 hover:bg-card",
              )}
            >
              {c.is_blocked ? (
                <Lock className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              ) : (
                <Building2 className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              )}
              <span className="text-sm">
                <span className="block font-medium">{c.customer_name}</span>
                <span className="block text-muted-foreground">
                  {c.is_blocked
                    ? "Not available right now. Please contact your Pollards account manager."
                    : linkedCustomerAccessSummary(c)}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="size-4 accent-primary"
          checked={remember}
          onChange={(e) => setRemember(e.target.checked)}
        />
        Remember my choice on this device
      </label>
      <Button
        className="w-full"
        disabled={!selected}
        onClick={() => selected && open(selected, remember)}
      >
        {selected ? `Open ${selected.customer_name}` : "Choose a customer"}
      </Button>
      <Card className="p-3 text-center text-xs text-muted-foreground">
        You can change customer at any time from the menu at the top right.
      </Card>
    </PortalSignInShell>
  );
}

export function PortalChooseCustomerPage() {
  return (
    <PortalSignInOutcomeGate
      kinds={["choose_customer", "welcome", "ready"] as const}
    >
      {(outcome, scenario) => (
        <ChooserBody outcome={outcome} scenario={scenario} />
      )}
    </PortalSignInOutcomeGate>
  );
}
